// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FloatingTooltip, { FloatingTooltipStyles } from "@eisinc/App/Components/FloatingTooltip/FloatingTooltip";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import XngMiniCart from "@eisinc/App/Components/XngMiniCart/XngMiniCart";
import { xHeaderState } from "@eisinc/App/Store/Reducers";
import Cart from "@eisinc/XDL/Icons/Cart";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import mixpanel from "mixpanel-browser";
import React, { FC, useEffect, useLayoutEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

// XNG-Changes: add xHeaderState
const mapStateToProps = (state: ApplicationState & xHeaderState) => {
    // <XNG-Changes>
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    // </XNG-Changes>
    return {
        // XNG-Changes: use cartState
        shouldLoadCart: !cartState.value && !cartState.isLoading,
        totalCountDisplay: cartState.value?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Changes
        cart,
        isLoading: cartState.isLoading,
        orderSubTotalDisplay: cartState.value?.orderSubTotalDisplay,
        isSticky: state.xHeader.isSticky,
        customerType: getSession(state).properties?.["mpExistingCustomer"],
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-Changes
    routerLinkStyles?: InjectableCss;
    cartTotalStyles?: InjectableCss;
    cartIconStyles?: InjectableCss;
    cartIconCountWrapper?: InjectableCss;
    cartCountStyles?: InjectableCss;
    cartLinkWrapper?: InjectableCss;
    tooltip?: FloatingTooltipStyles;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

// <XNG-Changes>
interface CartCountProps {
    lineCount: number | undefined;
    icon: any;
    isSticky: boolean | undefined;
}

const CartCount = ({ lineCount, icon, isSticky }: CartCountProps) => {
    return (
        <StyledWrapper {...cartLinkStyles.cartIconCountWrapper}>
            <StyledWrapper
                data-test-selector="cartLinkQuantity"
                {...cartLinkStyles.cartCountStyles}
                className={isSticky ? "sticky" : ""}
            >
                <Typography as="span">{lineCount || 0}</Typography>
            </StyledWrapper>
            <Icon src={icon} color="text.main" size={26} {...cartLinkStyles.cartIconStyles} />
        </StyledWrapper>
    );
};

interface CartTotalProps {
    cartTotal: string | undefined;
    isSticky: boolean;
    isLoading: boolean;
}

const CartTotal = ({ cartTotal, isSticky, isLoading }: CartTotalProps) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const measuredWidthRef = useRef<string>(null);

    useLayoutEffect(() => {
        if (!isLoading && textRef.current) {
            const width = textRef.current.getBoundingClientRect().width;
            measuredWidthRef.current = `${width}px`;
        }
    }, [isLoading, cartTotal]);

    return (
        <Hidden
            below="md"
            as="span"
            {...cartLinkStyles.cartTotalStyles}
            className={isSticky ? "sticky" : ""}
            {...{ ref: textRef }}
        >
            {isLoading ? <Skeleton height="17px" width={measuredWidthRef.current ?? "38px"} /> : cartTotal}
        </Hidden>
    );
};
// </XNG-Changes>

const iconMap = {
    // cart: ShoppingCart,
    // truck: Truck,
    // bag: ShoppingBag,
    // none: undefined,
    // XNG-Changes: change icons
    cart: Cart,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

// XNG-Changes
const styles = cartLinkStyles;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    // XNG-Changes
    isSticky,
    cart,
    orderSubTotalDisplay,
    isLoading,
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    customerType,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    // const showIcon = visibilityState !== "label";
    // const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    // XNG-Changes
    // const icon = iconMap[(showIcon && selectedIcon) || "none"];
    const icon = iconMap[selectedIcon];

    // XNG-Changes
    const { routerLinkStyles, cartTotalStyles } = styles;
    const cartSubTotal = orderSubTotalDisplay;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLinkStyles}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLinkStyles?.icon, iconProps: { src: icon, ...routerLinkStyles?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }
    // <XNG-Changes>
    const MiniCartButton = (
        <StyledWrapper>
            <Clickable {...routerLinkStyles} href={cartUrl} data-test-selector="cartLink">
                <CartCount lineCount={retainedTotalCountToDisplay} isSticky={isSticky} icon={icon} />
                <CartTotal cartTotal={cartSubTotal} isSticky={isSticky} isLoading={isLoading} {...cartTotalStyles} />
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Clickable>
        </StyledWrapper>
    );

    const onOpen = (open: boolean) => {
        // eslint-disable-next-line no-prototype-builtins
        if (open && (window as any).hasOwnProperty("mixpanelInitialized")) {
            mixpanel.track("Mini-Cart Pageview", { customer_type: customerType || "new" });
        }
    };
    // </XNG-Changes>

    return (
        // XNG-Changes: change structure
        <StyledWrapper {...styles.cartLinkWrapper}>
            <Hidden below="lg">
                <StyledWrapper>
                    <FloatingTooltip
                        trigger="hover"
                        placement="bottom-end"
                        offset={12}
                        overlay={true}
                        delay={{ open: 200, close: 550 }}
                        triggerComponent={MiniCartButton}
                        extendedStyles={styles.tooltip}
                        disableFlip={true}
                        onOpenChange={onOpen}
                    >
                        <XngMiniCart />
                    </FloatingTooltip>
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">{MiniCartButton}</Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    // {
                    //     displayName: "Show Both",
                    //     value: "both",
                    // },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    // {
                    //     displayName: "Show Icon Only",
                    //     value: "icon",
                    // },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    // {
                    //     displayName: "None",
                    //     value: "none",
                    // },
                    // {
                    //     displayName: "Bag",
                    //     value: "bag",
                    // },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    // {
                    //     displayName: "Truck",
                    //     value: "truck",
                    // },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
